import { computed, flow, IObservableArray, observable, runInAction, toJS } from 'mobx';
import API from 'api';
import { prepareLoopBackFilter } from '../utilities/utilities';
import Role from 'stores/Role';
import AccountPrescriberPractice from 'stores/AccountPrescriberPractice';
import { rootStore } from 'stores/RootStore';
import * as yup from 'yup';
import Query from 'utilities/Query';
import Prescriber from './Prescriber';

export type AssignableUser = {
	id: number,
	title: string,
	firstName: string,
	lastName: string
};

export default class Account {
	@observable id: number;
	@observable mfaCode: string;
	@observable mfaExpiry: string = '';
	@observable realm: string = '';
	@observable username: string = '';
	@observable title: string = '';
	@observable firstName: string = '';
	@observable lastName: string = '';
	@observable phoneNumber: string = '';
	@observable email: string = '';
	@observable password: string;
	@observable emailVerified: boolean = false;
	@observable Roles: IObservableArray<Role> = [];
	@observable disabled: boolean = false;
	@observable scheduledReminders: boolean = false;
	@observable simpleLoginAllowed: boolean = false;
	@observable invitationSent: boolean = false;
	@observable alertSMS: boolean = false;
	@observable alertEmail: boolean = false;
	@observable raNumber: string = '';
	@observable updatedRoleId: number;
	@observable AccountPrescriberPractices: IObservableArray<AccountPrescriberPractice> = [];
	createManagerRole: boolean;
	createViewerRole: boolean;

	constructor(data: Account) {
		if (data) this.extend(data);
	}

	extend(data: Account = {}) {
		runInAction(() => {
			Object.assign(this, data);
		});
	}

	static ValidationSchemaPasswordChange = yup.object().shape({
		oldPassword: yup.string().required('Old password is required'),
		newPassword: yup
			.string()
			.label('Password')
			.min(5)
			.required(),
		newPasswordConfirm: yup.string().oneOf([yup.ref('newPassword')], "Passwords don't match")
	});

	static ValidationSchemaAccount = yup.object().shape({
		firstName: yup
			.string()
			.label('First name')
			.required(),
		lastName: yup
			.string()
			.label('Last name')
			.required(),
		email: yup
			.string()
			.email()
			.label('E-mail')
			.required(),
		phoneNumber: yup
			.string()
			.nullable()
			.matches(/^[0-9]*$/, {
				message: 'Only numeric characters are allowed for Mobile',
				excludeEmptyString: true
			}),
		raNumberUnique: yup
			.string()
			.label('RA Number has to be unique.')
			.when('isRaNumberUnique', {
				is: false,
				then: yup.string().required('RA Number has to be unique.')
			}),
		raNumber: yup
			.string()
			.label('RA Number')
			.when('isRaNumberRequired', {
				is: true,
				then: yup.string().required()
			})
	});

	@computed
	get fullName() {
		if (this.firstName || this.lastName) {
			return `${this.firstName} ${this.lastName}`;
		} else {
			return this.email;
		}
	}

	@computed
	get role(): string {
		let roleId = this.Roles?.[0]?.roleId;
		switch (roleId) {
			case 1:
				return 'manager';
			case 2:
				return 'user';
			case 3:
				return 'admin';
			case 4:
				return 'viewer';
			default:
				return 'user';
		}
	}
	reload = async loadSpecial => {
		try {
			let account = await Account.loadById(this.id, loadSpecial);
			this.extend(toJS(account));
			return true;
		} catch (err) {
			throw err;
		}
	};

	static loadByRaNumber = async (raNumber: string): Promise<Array<Account>> => {
		return await API.get(
			`/Accounts?filter=${JSON.stringify({
				where: { raNumber: raNumber },
				fields: {
					id: true,
					raNumber: true
				}
			})}`
		);
	};

	static load(filters): Promise<Array<Account>> {
		return new Promise((resolve, reject) => {
			let extFilters = {
				...{ include: ['Manager'] },
				...filters
			};
			extFilters = prepareLoopBackFilter(extFilters);
			API.get(`/Account?filter=${JSON.stringify(extFilters)}`)
				.then(results => {
					resolve(results.map(el => new Account(el)));
				})
				.catch(err => {
					reject(err);
				});
		});
	}

	static loadById = async (id: number, loadSpecial): Account => {
		let user = rootStore.auth.user;
		if (
			user.role === 'patient' ||
			user.role === 'practitioner' ||
			user.role === 'admin' ||
			user.role === 'viewer' ||
			user.id === id
		) {
			let user = await API.get(
				`/Accounts/${id}?filter=${JSON.stringify({
					include: [
						'Roles',
						{ AccountPrescriberPractices: { PrescriberPractice: ['Practice', 'Prescriber'] } }
					]
				})}`
			);
			return new Account(user);
		} else if (loadSpecial === 'practitioner') {
			let results = await API.get(
				`/Accounts/practitionersByManager/${user.id}?filter=${JSON.stringify({
					where: { id: id }
				})}`
			);
			return new Account(results[0]);
		} else {
			let results = await API.get(
				`/Accounts/byManager/${user.id}?filter=${JSON.stringify({
					where: { id: id }
				})}`
			);
			return new Account(results[0]);
		}
	};

	changePassword = flow(function*(data: {
		oldPassword: string,
		newPassword: string,
		newPasswordConfirm: string
	}) {
		yield Account.ValidationSchemaPasswordChange.validate(toJS(data));
		yield API.post(
			`/Accounts/change-password`,
			{ oldPassword: data.oldPassword, newPassword: data.newPassword },
			'form-urlencoded'
		);
	});

	update = flow(function*(withData) {
		if (withData) this.extend(withData);
		yield Account.ValidationSchemaAccount.validate(toJS(this), { abortEarly: false });
		if (this.id) {
			// Update record
			yield API.patch(`/Accounts/${this.id}`, toJS(this));
		} else {
			// New record
			let result = yield API.post(`/Accounts`, toJS(this));
			this.id = result.id;
		}

		const chunks = [];
		const chunkSize = 4;
		for (let i = 0; i < this.AccountPrescriberPractices.length; i += chunkSize) {
			const chunk = this.AccountPrescriberPractices.slice(i, i + chunkSize);
			chunks.push(chunk);
		}
		for (const chunk of chunks) {
			yield Promise.all(
				chunk.map(app => {
					app.accountID = this.id;
					return new AccountPrescriberPractice(app).update();
				})
			);
		}
	});

	static async notifyPrescriber(ids: Array<int>) {
		return await API.post('/Accounts/notify', { ids });
	}

	static getManagers = async (id: number): Promise<Array<AssignableUser>> => {
		return await API.get(`/Accounts/managers/${id}`);
	};

	static loadUsers(query?: Query): Promise<{ itemCount: number, items: Array<Account> }> {
		if (!query) {
			query = new Query();
			query.pageSize = 1000;
		}
		return new Promise((resolve, reject) => {
			API.get(`/Accounts/users/${query.toToLpQueryUrl()}`)
				.then(result => {
					let res = {
						itemCount: result.count,
						items: result.items.map(el => new Account(el))
					};
					resolve(res);
				})
				.catch(err => {
					reject(err);
				});
		});
	}

	static loadUsersByRole(
		query?: Query,
		role
	): Promise<{ itemCount: number, items: Array<Account> }> {
		if (!query) {
			query = new Query();
			query.pageSize = 1000;
		}
		return new Promise((resolve, reject) => {
			API.get(`/Accounts/users/${role}/${query.toToLpQueryUrl()}`)
				.then(result => {
					let res = {
						itemCount: result.count,
						items: result.items.map(el => new Account(el))
					};
					resolve(res);
				})
				.catch(err => {
					reject(err);
				});
		});
	}
}



// WEBPACK FOOTER //
// ./src/stores/Account.js